import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { proxyCoverUrl } from "../utils/coverProxy";
import { 
  BookOpen, Star, ArrowLeft, ShoppingCart, User, 
  Send, Home, MessageCircle, BookMarked, Users, Lightbulb, Feather
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import REFLECTIONS from "../data/reflections";

export default function BookDetailPage() {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [newFeedback, setNewFeedback] = useState({ visitor_name: "", comment: "" });

  useEffect(() => {
    const fetchData = async (retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          const [bookRes, feedbackRes] = await Promise.all([
            axios.get(`${API}/books/${bookId}`),
            axios.get(`${API}/books/${bookId}/feedback`)
          ]);
          setBook(bookRes.data);
          setFeedback(feedbackRes.data);
          setLoading(false);
          axios.post(`${API}/track-book-view/${bookId}`).catch(() => {});
          return;
        } catch (error) {
          if (i === retries - 1) {
            console.error("Failed to fetch book:", error);
            toast.error("Failed to load book details");
            setLoading(false);
          } else {
            await new Promise(r => setTimeout(r, 1500));
          }
        }
      }
    };
    fetchData();
  }, [bookId]);

  useEffect(() => {
    if (book) {
      document.title = book.title + " by " + book.author + " | The Reading Room";
    }
  }, [book]);

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!newFeedback.visitor_name.trim() || !newFeedback.comment.trim()) {
      toast.error("Please fill in your name and comment");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/books/${bookId}/feedback`, newFeedback);
      setFeedback([response.data, ...feedback]);
      setNewFeedback({ visitor_name: "", comment: "" });
      toast.success("Thank you for your feedback!");
    } catch (error) {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--highlight)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--bg)' }}>
        <BookOpen className="w-16 h-16 mb-4" style={{ color: '#C2B096' }} />
        <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text)' }}>Book not found</h2>
        <Link to="/browse">
          <Button className="rounded-full" style={{ background: '#5D4228', color: '#F5F0E7' }}>
            Back to Library
          </Button>
        </Link>
      </div>
    );
  }

  const isPlaceholder = !book.cover_image || book.cover_image.includes('placehold.co') || book.cover_image.includes('via.placeholder');
  const showFallback = isPlaceholder || imgError;
  const fallbackColors = ["from-amber-900 to-amber-700", "from-stone-800 to-stone-600", "from-emerald-900 to-emerald-700", "from-yellow-900 to-yellow-700"];
  const colorIdx = book.title.length % fallbackColors.length;

  const pageDescription = book.what_to_expect || "Read " + book.title + " by " + book.author + ". Explore key learnings, reflections, and reader feedback at The Reading Room.";
  const ogTitle = book.title + " by " + book.author;
  const ogDescription = book.what_to_expect || "Discover " + book.title + " at The Reading Room.";
  const canonicalUrl = "https://thereading-room.com/book/" + book.book_id;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <Helmet>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="book" />
        {book.cover_image && !book.cover_image.includes('placehold') && (
          <meta property="og:image" content={book.cover_image} />
        )}
      </Helmet>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: 'rgba(245,240,231,0.9)', backdropFilter: 'blur(12px)', borderColor: 'var(--highlight-border)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BookOpen className="w-6 h-6" style={{ color: 'var(--muted)' }} />
            <span className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--text)' }}>
              The Reading Room
            </span>
          </Link>
          <Link to="/browse">
            <Button variant="ghost" className="gap-2" style={{ color: 'var(--muted)' }}>
              <ArrowLeft className="w-4 h-4" />
              Back to Library
            </Button>
          </Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Book Header */}
        <div className="grid md:grid-cols-3 gap-12 mb-16">
          {/* Cover */}
          <div className="md:col-span-1">
            <div className="book-cover aspect-[2/3] rounded-lg overflow-hidden shadow-xl max-w-sm mx-auto md:mx-0" style={{ background: 'var(--skeleton)' }}>
              {showFallback ? (
                <div className={`w-full h-full bg-gradient-to-br ${fallbackColors[colorIdx]} flex flex-col items-center justify-center p-6 text-center`}>
                  <BookOpen className="w-12 h-12 text-white/40 mb-3" />
                  <h4 className="text-white font-bold text-lg leading-tight mb-2">{book.title}</h4>
                  <p className="text-white/70 text-sm">{book.author}</p>
                </div>
              ) : (
                <img
                  src={proxyCoverUrl(book.cover_image)}
                  alt={`Cover of ${book.title}`}
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              )}
            </div>
          </div>

          {/* Details */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#3A2E1E' }}>
                {book.title}
              </h1>
              <p className="text-xl" style={{ color: 'var(--muted)' }}>by {book.author}</p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {book.rating && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'var(--bg-alt2)' }}>
                  <Star className="w-5 h-5 fill-amber-500 text-amber-500" />
                  <span className="font-semibold" style={{ color: 'var(--muted)' }}>{book.rating}</span>
                </div>
              )}
              {book.publisher && (
                <span className="text-sm font-mono" style={{ color: 'var(--muted)' }}>
                  Published by {book.publisher}
                </span>
              )}
              {book.category && (
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'var(--skeleton)', color: '#5C6B3C' }}>
                  {book.category}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: 'var(--bg-alt2)', border: '1px solid var(--highlight-border)' }}>
              <Home className="w-4 h-4" style={{ color: 'var(--muted)' }} />
              <span className="text-sm" style={{ color: 'var(--muted2)' }}>Physical copy available from our home library</span>
            </div>

            <div className="flex flex-wrap gap-3">
              {book.amazon_link && (
                <a href={book.amazon_link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-90"
                  style={{ background: '#5D4228', color: '#F5F0E7' }}
                  data-testid="amazon-link"
                >
                  <ShoppingCart className="w-4 h-4" /> Buy on Amazon
                </a>
              )}
              {book.flipkart_link && (
                <a href={book.flipkart_link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-90"
                  style={{ background: '#795A3B', color: '#F5F0E7' }}
                  data-testid="flipkart-link"
                >
                  <ShoppingCart className="w-4 h-4" /> Buy on Flipkart
                </a>
              )}
            </div>

            {/* Reflection badge */}
            {(() => {
              const match = REFLECTIONS.find(r => r.title.toLowerCase() === book.title.toLowerCase());
              if (!match) return null;
              return (
                <Link
                  to="/reflections"
                  className="flex items-center gap-3 px-5 py-3.5 rounded-xl transition-all hover:opacity-90"
                  style={{ background: 'var(--highlight-muted)', border: '1px solid rgba(209,168,110,0.25)' }}
                  data-testid="reflection-link"
                >
                  <Feather className="w-4.5 h-4.5 flex-shrink-0" style={{ color: '#8B4513' }} strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Read my reflection</p>
                    <p className="text-[11px] leading-snug mt-0.5" style={{ color: 'var(--muted)' }}>{match.intro}</p>
                  </div>
                </Link>
              );
            })()}
          </div>
        </div>

        {/* Book Details Sections */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {book.what_to_expect && (
            <div className="rounded-xl p-6 shadow-sm" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border)' }}>
              <div className="flex items-center gap-2 mb-4" style={{ color: 'var(--muted)' }}>
                <BookMarked className="w-5 h-5" />
                <h3 className="font-semibold">What to Expect</h3>
              </div>
              <p className="leading-relaxed text-sm" style={{ color: 'var(--muted2)' }}>{book.what_to_expect}</p>
            </div>
          )}
          {book.key_learnings && (
            <div className="rounded-xl p-6 shadow-sm" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border)' }}>
              <div className="flex items-center gap-2 mb-4" style={{ color: '#5C6B3C' }}>
                <Lightbulb className="w-5 h-5" />
                <h3 className="font-semibold">Key Learnings</h3>
              </div>
              <p className="leading-relaxed text-sm" style={{ color: 'var(--muted2)' }}>{book.key_learnings}</p>
            </div>
          )}
          {book.who_should_read && (
            <div className="rounded-xl p-6 shadow-sm" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border)' }}>
              <div className="flex items-center gap-2 mb-4" style={{ color: 'var(--on-hero-muted)' }}>
                <Users className="w-5 h-5" />
                <h3 className="font-semibold">Who Should Read This</h3>
              </div>
              <p className="leading-relaxed text-sm" style={{ color: 'var(--muted2)' }}>{book.who_should_read}</p>
            </div>
          )}
        </div>

        {/* Special Section for "Which Game to Play" — 4 Innings Framework + Video */}
        {book.book_id === "book_b7a490d70fc7" && (
          <div className="space-y-10 mb-16">
            {/* Video Section */}
            <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border)' }}>
              <div className="p-6 pb-3">
                <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#3A2E1E' }}>Watch: The Strategic Framework in Action</h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>A glimpse into the ideas behind "Which Game to Play"</p>
              </div>
              <div className="px-6 pb-6">
                <video
                  controls
                  className="w-full rounded-xl"
                  style={{ maxHeight: '480px', background: '#000' }}
                  poster="https://customer-assets.emergentagent.com/job_4972be2e-528e-4972-9e9c-ce8c3f58d5ba/artifacts/6s2y84gg_616xvjk0PdL._SY522_.jpg"
                  data-testid="book-video"
                >
                  <source src="/which-game-to-play-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* 4 Innings Framework Image */}
            <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border)' }}>
              <div className="p-6 pb-3">
                <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#3A2E1E' }}>From Framework to Action: Four Innings</h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>Working models your leadership team can run this quarter — drawn straight from the book.</p>
              </div>
              <div className="p-6 pt-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_4972be2e-528e-4972-9e9c-ce8c3f58d5ba/artifacts/t1k5qwd4_4%20innings.png"
                  alt="Four Innings Framework — The Portfolio Map, The Verb Test, The Grove Test, The Generative Cycle Audit"
                  className="w-full rounded-xl"
                  data-testid="four-innings-image"
                />
              </div>
            </div>
          </div>
        )}

        {/* Feedback Section */}
        <div className="rounded-2xl p-8 shadow-sm" style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border)' }}>
          <div className="flex items-center gap-2 mb-8">
            <MessageCircle className="w-6 h-6" style={{ color: 'var(--muted)' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--text)' }}>
              Reader Thoughts
            </h2>
            <span className="ml-2 px-2 py-1 rounded-full text-sm" style={{ background: 'var(--bg-alt2)', color: 'var(--muted)' }}>
              {feedback.length}
            </span>
          </div>

          <form onSubmit={handleSubmitFeedback} className="mb-10 p-6 rounded-xl" style={{ background: 'var(--bg-alt2)' }}>
            <h3 className="font-semibold mb-4" style={{ color: 'var(--text)' }}>Share your thoughts about this book</h3>
            <div className="space-y-4">
              <Input
                data-testid="feedback-name"
                placeholder="Your name"
                value={newFeedback.visitor_name}
                onChange={(e) => setNewFeedback({ ...newFeedback, visitor_name: e.target.value })}
                style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight)' }}
              />
              <Textarea
                data-testid="feedback-comment"
                placeholder="What did you think of this book? Share your experience..."
                value={newFeedback.comment}
                onChange={(e) => setNewFeedback({ ...newFeedback, comment: e.target.value })}
                rows={4}
                style={{ background: 'var(--card-bg)', borderColor: 'var(--highlight)' }}
              />
              <Button 
                type="submit"
                disabled={submitting}
                className="rounded-full px-6"
                style={{ background: '#5D4228', color: '#F5F0E7' }}
                data-testid="submit-feedback"
              >
                {submitting ? "Submitting..." : (<><Send className="w-4 h-4 mr-2" />Share Feedback</>)}
              </Button>
            </div>
          </form>

          {feedback.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#C2B096' }} />
              <p style={{ color: 'var(--muted)' }}>Be the first to share your thoughts on this book!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedback.map((item) => (
                <div key={item.feedback_id} className="rounded-xl p-5" style={{ background: 'var(--bg)', border: '1px solid #E8DCC8' }} data-testid={`feedback-item-${item.feedback_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--skeleton)' }}>
                      <User className="w-5 h-5" style={{ color: 'var(--muted)' }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: 'var(--text)' }}>{item.visitor_name}</p>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>
                        {new Date(item.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <p className="leading-relaxed text-sm" style={{ color: 'var(--muted2)' }}>{item.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="py-8 px-6 mt-20" style={{ background: 'var(--hero-dark)', borderTop: '1px solid var(--highlight-border-light)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <p className="text-sm" style={{ color: 'var(--on-hero-muted)' }}>Curated with love by Lopa</p>
        </div>
      </footer>
    </div>
  );
}
